import { BrandLogo, Checkbox, Tooltip } from '@kontentino/ui';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';
import clsx from 'clsx';
import type { TFunction } from 'i18next';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { stopPropagation } from 'utils/mouseEvent';
import {
  SocialLoginPageReason,
  SocialLoginPageRelation,
} from 'types/SocialLogin';

export type SocialProfileDisabledReason = {
  key: SocialLoginPageReason;
  businessName: string | null;
};

export type SocialProfile = {
  id: number | string;
  name: string;
  type: number;
  businessId?: string | null;
  businessName?: string | null;
  relation?: SocialLoginPageRelation;
  ownerBusinessName?: string | null;
  isDisabled?: boolean;
  disabledReason?: SocialProfileDisabledReason;
  logo?: {
    src: string;
  };
};

type Props = {
  profile: SocialProfile;
  isSelected: boolean;
  onClick: (id: SocialProfile['id']) => void;
  // A line under the name (e.g. the network), followed by the owner of a
  // shared page.
  subtitle?: string;
};

const getDisabledReasonText = (
  t: TFunction,
  profile: SocialProfile,
): string | undefined => {
  const reason = profile.disabledReason;

  if (!reason) {
    return undefined;
  }

  if (reason.key === 'outside_portfolios') {
    return t('newSocialMediaProfile.oboTokenType.outsidePortfoliosReason');
  }

  // The sentence names the portfolio; with no name to put in it, say nothing
  // rather than render a sentence with a hole in it.
  const name = reason.businessName ?? profile.ownerBusinessName;

  return name
    ? t('newSocialMediaProfile.oboTokenType.notAdminReason', { name })
    : undefined;
};

const SelectSocialProfileOption: FC<Props> = ({
  profile,
  isSelected,
  onClick,
  subtitle,
}) => {
  const { t } = useTranslation();
  const isDisabled = !!profile.isDisabled;
  const disabledReason = isDisabled
    ? getDisabledReasonText(t, profile)
    : undefined;
  const ownerName =
    profile.relation === 'shared' ? profile.ownerBusinessName : null;

  // Disabled rows are made inert here as well as by CSS, so a row can never
  // report a click no matter how the event reaches it.
  const handleClick = () => {
    if (!isDisabled) {
      onClick(profile.id);
    }
  };

  // Dims what a disabled row cannot do, but not its reason: that text must
  // stay readable.
  const dimmed = { 'tw-opacity-50': isDisabled };

  const ownedBy = ownerName ? (
    <span
      // Inside the subtitle line, which is dimmed already.
      className={clsx('tw-text-sm tw-text-grayscale-100', !subtitle && dimmed)}
      data-cy="select-social-profiles_row-owned-by"
    >
      {t('newSocialMediaProfile.oboTokenType.ownedBy', {
        name: ownerName,
      })}
    </span>
  ) : null;

  // The wrapper span is the tooltip trigger, so it sits OUTSIDE the
  // pointer-events-none element: otherwise hover never reaches it.
  const row = (
    <span
      className="tw-block"
      data-cy={`select-social-profiles_row_${profile.id}`}
    >
      <div
        className={clsx('tw-flex tw-items-center tw-justify-between', {
          'hover:tw-cursor-pointer': !isDisabled,
          'tw-pointer-events-none': isDisabled,
        })}
        onClick={handleClick}
      >
        <span className="tw-mr-2 tw-flex tw-items-center">
          <BrandLogo
            src={profile.logo?.src}
            name={profile.name}
            size={28}
            socialMediaType={getSocialMediaTypeForBrandLogo(profile.type)}
            iconSize={getYtLogoGuidelineSize(12, profile.type)}
            className={clsx('tw-mr-3 tw-shrink-0', dimmed)}
          />
          <span className="tw-flex tw-flex-col">
            <span
              className={clsx('tw-text-md tw-font-medium ', dimmed, {
                'tw-text-primary-100': isSelected,
              })}
            >
              {profile.name}
            </span>
            {subtitle ? (
              <span
                className={clsx('tw-text-sm tw-text-grayscale-100', dimmed)}
                data-cy="select-social-profiles_row-subtitle"
              >
                {subtitle}
                {ownedBy && <> · {ownedBy}</>}
              </span>
            ) : (
              ownedBy
            )}
            {disabledReason && (
              <span
                className="tw-text-sm tw-text-grayscale-140"
                data-cy="select-social-profiles_row-disabled-reason"
              >
                {disabledReason}
              </span>
            )}
          </span>
        </span>
        {/* Dims the box and its tick together. */}
        <span className={clsx('tw-flex tw-shrink-0', dimmed)}>
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            onChange={handleClick}
            onClick={stopPropagation()}
            size="small"
            title={profile.name}
          />
        </span>
      </div>
    </span>
  );

  // Only a row that has a reason to show pays for a tooltip: several hundred
  // enabled rows must not each carry a Tooltip wrapper (and its Tippy setup).
  // The reason is also text on the row, for keyboard and touch users; the
  // tooltip stays for mouse users.
  if (!disabledReason) {
    return row;
  }

  return <Tooltip content={disabledReason}>{row}</Tooltip>;
};

// Memoised: a list of several hundred rows re-renders only the rows whose
// props changed (the parent passes one stable `onClick`).
export default React.memo(SelectSocialProfileOption);
